import { bffCrmPersonCareKeySavings } from '@fireflyhealth/api'
import type { BffCrmPersonCareKeySavingsRetrieveResponse } from '@fireflyhealth/api'

import { useQuery } from '~/shared/hooks/query'
/**
 * Cache key for a member's Care Key savings. Module-private for now (the tab is read-only, so
 * nothing invalidates it yet); export it when a mutation needs to invalidate this query.
 */
const personCareKeySavingsQueryKey = (personId: number) =>
  bffCrmPersonCareKeySavings.retrieveQueryKey({ person_id: personId })

/**
 * Fetches a member's Care Key savings from the CRM BFF
 * (`GET /bff/crm/person/<person_id>/care-key-savings/`).
 *
 * Not paginated — a member-year of specialist office visits is small, so the server returns the
 * whole payload: summary totals, the per-claim list, and missed savings already rolled up by
 * provider. Every derived value, including `isCareKeyCandidate`, is computed server-side; this
 * layer only renders what it is given.
 *
 * Disabled until a `personId` is available, so nothing is requested for a member the tab has not
 * resolved yet.
 */
export const useGetPersonCareKeySavings = (variables: { personId: number | undefined }) => {
  const { personId } = variables
  return useQuery<BffCrmPersonCareKeySavingsRetrieveResponse>(
    personId != null
      ? personCareKeySavingsQueryKey(personId)
      : ['retrieveBffCrmPersonCareKeySavings', {}],
    async () => (await bffCrmPersonCareKeySavings.retrieve({ person_id: Number(personId) })).data,
    { enabled: personId != null },
    { error: 'Failed to fetch Care Key savings' },
  )
}
